<script>
	import { onMount } from "svelte";
	// import { fly } from "svelte/transition";
	import Scroller from "@sveltejs/svelte-scroller";

	// import anime from "animejs/lib/anime.es.js";
	import MediaQuery from "./components/MediaQuerySsr.svelte";
	import Header from "./components/Header.svelte";

	import { cover, slides, bildnachweise } from "./config";
	import Cover from "./sections/0_intro/Cover.svelte";
	import Bildnachweise from "./sections/Bildnachweise.svelte";
	import Slide from "./components/Slide.svelte";
	import { headerBlack, headerToc, headerState } from "./stores";

	// ///////////////////////////////////////////////////////////
	// props
	// ///////////////////////////////////////////////////////////
	export let name;
	export let forSubscribers = false;

	const slidesShown = forSubscribers
		? slides.filter((s) => !(s.hideFor && s.hideFor.includes("subscribers")))
		: slides.filter((s) => !(s.hideFor && s.hideFor.includes("default")));

	// ///////////////////////////////////////////////////////////
	// media query variables
	// ///////////////////////////////////////////////////////////
	let breakpoint;
	let platform;
	let orientation;
	let mobile;
	let tablet;

	// ///////////////////////////////////////////////////////////
	// constants and definitions
	// ///////////////////////////////////////////////////////////

	// ///////////////////////////////////////////////////////////
	// scroller variables
	// ///////////////////////////////////////////////////////////
	let count;
	let index;
	let offset;
	let progress;
	let top = 0;
	let threshold = 0.75;
	let bottom = 0;

	$: progressDelay = ["mobile", "tablet"].includes(platform) ? 0.15 : 0.05; // 15% percent of slide
	$: pastSlideOffset = ["mobile", "tablet"].includes(platform) ? 0.25 : 0;

	const observerMargin = 1000;

	let currentSlideObj = cover;

	$: currentSlideObj =
		offset && offset > 0 ? (index === 0 ? cover : slidesShown[index - 1]) : currentSlideObj === cover ? cover : null;
	$: currentSlide = currentSlideObj && currentSlideObj.id;
	$: currentSection = currentSlideObj && currentSlideObj.section;

	$: {
		if (currentSlideObj) {
			headerBlack.set(currentSlideObj.section === "fragen");
		} else {
			headerBlack.set(false);
		}
	}

	$: {
		if (currentSlideObj) {
			if (currentSlideObj.section === "intro") headerToc.set("");
			else if (currentSlideObj.section === "produkt") headerToc.set("produkt");
			else if (currentSlideObj.section === "produkt_national") headerToc.set("national");
			else if (currentSlideObj.section === "produkt_berlin") headerToc.set("berlin");
			else if (currentSlideObj.section === "produkt_wochenende") headerToc.set("wochenende");
			else headerToc.set("");
		} else {
			headerToc.set("");
		}
	}

	$: {
		let s = "initial";
		if (currentSlideObj) {
			if (["produkt", "produkt_national", "produkt_berlin"].indexOf(currentSlideObj.section) !== -1) s = "toc";
			else if (["fragen", "chefs"].indexOf(currentSlideObj.section) !== -1) s = "simple";
			else if (["intro"].indexOf(currentSlideObj.section) !== -1) s = "initial";
			else s = "toc";
		} else {
			if (progress > 0.9) s = "toc";
		}
		headerState.set(s);
	}

	// ///////////////////////////////////////////////////////////
	// debug
	// ///////////////////////////////////////////////////////////
	// $: console.log("currentSlide", currentSlide, index);
	// $: console.log("progress", offset, getProgress(offset));
	// $: console.log("offset", offset);

	// setup lazy loading, per section

	let container;

	function loadLazyImages(slide) {
		slide.querySelectorAll(".lazy-img.lazy-img-notresponsive").forEach((e) => {
			if (!e.getAttribute("src")) e.setAttribute("src", e.getAttribute("data-lazy-src"));
		});

		slide.querySelectorAll(".lazy-img.lazy-img-responsive").forEach((e) => {
			if (!e.getAttribute("src")) e.setAttribute("src", e.getAttribute("data-lazy-src"));
		});
		slide.querySelectorAll(".lazy-img.lazy-img-responsive-" + breakpoint).forEach((e) => {
			if (!e.getAttribute("src")) e.setAttribute("src", e.getAttribute("data-lazy-src"));
		});
	}

	let makeBgFade = false;

	onMount(() => {
		setTimeout(() => {
			makeBgFade = true;
		}, 1000);

		document.documentElement.style.setProperty(
			"--scrollbar-width",
			window.innerWidth - document.documentElement.clientWidth + "px"
		);

		const observerOptions = {
			rootMargin: `${observerMargin}px`,
		};

		const observerCallback = (entries, observer) => {
			entries.forEach((e) => {
				if (e.isIntersecting) {
					loadLazyImages(e.target);
					//observer.unobserve(e.target);
				}
			});
		};

		const observer = new IntersectionObserver(observerCallback, observerOptions);

		container.querySelectorAll("section").forEach((e) => {
			observer.observe(e);
		});

		window.addEventListener("resize", () => {
			container.querySelectorAll("section").forEach((e) => {
				if (
					e.getBoundingClientRect().top > -observerMargin &&
					e.getBoundingClientRect().top < window.innerHeight + observerMargin
				) {
					loadLazyImages(e);
				}
			});
		});
	});

	let isintro = true;
	$: isintro =
		(currentSlideObj && currentSlideObj.id === "cover") || (typeof window !== "undefined" && window.pageYOffset === 0);

	let iscta = false;
	$: iscta = !forSubscribers && (currentSlide === "print" || currentSlide === "bestehend");

	function getProgress(offset) {
		return Math.min(1, Math.max(0, offset - progressDelay) / (1 - progressDelay));
	}
</script>

<Header {forSubscribers} />
<MediaQuery bind:breakpoint bind:platform bind:orientation bind:mobile bind:tablet />

<div class="relaunch-scrolly scroller" class:for-subscribers={forSubscribers} bind:this={container}>
	<Scroller {top} {threshold} {bottom} bind:count bind:index bind:offset bind:progress>
		<div
			slot="background"
			class:showintro={isintro}
			class:showcta={iscta}
			class:fade={makeBgFade}
			class:currentFixed={currentSlideObj && currentSlideObj.props.backgroundFixed}
			style:background-color={getProgress(offset) > 0.5 && currentSlideObj && currentSlideObj.props.background50
				? currentSlideObj.props.background50
				: currentSlideObj && currentSlideObj.props.background
				? currentSlideObj.props.background
				: "transparent"}
		>
			<div class="gradient-animated intro" />
			<div class="gradient-animated cta" />
		</div>

		<div slot="foreground">
			<Cover id={cover.id} {...cover.props} {forSubscribers} />

			{#each slidesShown as slide, i}
				<Slide
					component={slide.component}
					id={slide.id}
					section={slide.section}
					slideProgress={i === index - 1 ? getProgress(offset) : i < index - 1 ? 1 : 0}
					{...slide.props}
					{forSubscribers}
					current={slide === currentSlideObj}
					past={(i === index - 2 && getProgress(offset) > pastSlideOffset) || i < index - 2}
				/>
			{/each}
			<Bildnachweise id={bildnachweise.id} {...bildnachweise.props} {forSubscribers} />
		</div>
	</Scroller>
</div>

<style lang="scss">
	.relaunch-scrolly {
		// font-family: $ff-sans;
		// font-size: $fs-base;
		// line-height: $lh-base;
		// color: $color;
	}

	// ///////////////////////////////////////////////////////////
	// scroller
	// ///////////////////////////////////////////////////////////
	.scroller {
		margin-bottom: 0;
		z-index: 0;
		position: relative;
	}

	[slot="background"] {
		overflow: hidden;
		height: 100vh;
		will-change: background-color;
		transform: translateZ(0);
	}

	[slot="foreground"] {
		pointer-events: none;
	}

	:global(section#cover) {
		//pointer-events: all;
		//overflow: hidden;
		z-index: 1;
		position: relative;
	}

	:global([slot="foreground"] section) {
		pointer-events: all;
		height: 100vh;
		overflow: hidden;

		// desktop
		@media (min-width: #{$breakpoint-tablet + 1px}) {
			max-height: 1300px;
			min-height: 1120px;
		}

		// tablets
		@media (min-width: #{$breakpoint-mobile + 1px}) and (max-width: $breakpoint-tablet) {
			max-height: 1300px;
			min-height: 900px;
		}

		// phones
		@media (max-width: $breakpoint-mobile) {
			max-height: 850px;
			min-height: 700px;
		}
	}

	// ///////////////////////////////////////////////////////////
	// breakpoints
	// ///////////////////////////////////////////////////////////

	// desktop
	@media (min-width: #{$breakpoint-tablet + 1px}) {
	}

	// tablets
	@media (min-width: #{$breakpoint-mobile + 1px}) and (max-width: $breakpoint-tablet) {
	}

	// phones
	@media (max-width: $breakpoint-mobile) {
	}

	[slot="background"] .gradient-animated {
		position: absolute;
		left: 0;
		right: 0;
		bottom: 0;
		top: 0;
		background-size: 150% 150%;
		opacity: 0;
		background-attachment: fixed;
	}

	[slot="background"].fade {
		transition: background-color 1s ease;

		.gradient-animated {
			transition: opacity 1s ease;
		}
	}

	[slot="background"] .intro {
		opacity: 0;
		background-image: linear-gradient(
			5.8deg,
			#000000 1.5%,
			#31084d 15.51%,
			#701eb0 35%,
			#e35b79 64.85%,
			#f9a0d0 89.22%,
			#ffcaca 104.45%,
			#ffcaca 118.46%
		);
		animation: gradient-animation 10s ease infinite;
	}
	@keyframes gradient-animation {
		0% {
			background-position: 40% 10%;
		}
		50% {
			background-position: 60% 100%;
		}
		100% {
			background-position: 40% 10%;
		}
	}

	[slot="background"].showintro .intro {
		opacity: 1;
	}

	[slot="background"] .cta {
		opacity: 0;
		background-image: linear-gradient(0deg, #e62565 0%, #f7af6f 77.97%, #faf0c3 100%);
		animation: gradient-animation 5s ease infinite;
	}

	[slot="background"].showcta .cta {
		opacity: 1;
	}
</style>
